// ProfessionalDetailsPage.jsx

// Dependencies
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

// Components
import ProfessionalDetails from '@/components/data/professionals/ProfessionalDetails';

// Services
import { getProfessional, getProfessionalHoursReport, getStartDates } from '@/services/schedulingService';

const ProfessionalDetailsPage = () => {
  const { id } = useParams();

  const [professional, setProfessional] = useState(null);
  const [engagements, setEngagements] = useState(null);
  const [startDates, setStartDates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // showSpinner is off for in-place refreshes (notes, skills, PTO, allocations)
  // so editing a field does not blank the whole page behind the loading spinner.
  const fetchData = async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const startDates = await getStartDates(false, false, 52); // Get 52 weeks of dates
      setStartDates(startDates);

      const professional = await getProfessional(id);
      if (!professional) {
        // The API answers 200 with an empty result for an unknown ID.
        setProfessional(null);
        setEngagements(null);
        setError(`No professional found with ID "${id}".`);
        return;
      }
      setProfessional(professional);

      const engagements = await getProfessionalHoursReport(id);
      setEngagements(engagements);
    } catch (err) {
      console.error('Error fetching professional data:', err);
      setProfessional(null);
      setEngagements(null);
      setError(err?.message || 'Failed to load this professional. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleUpdate = () => {
    fetchData({ showSpinner: false });
  };

  const pageStyles = {
    container: {
      minHeight: 0,
      background: 'var(--canvas)',
      padding: '20px'
    },
    breadcrumbContainer: {
      marginBottom: '2rem'
    },
    breadcrumb: {
      background: '#ffffff',
      borderRadius: '8px',
      padding: '1rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem'
    },
    breadcrumbLink: {
      color: '#86bc25',
      textDecoration: 'none',
      fontWeight: '500'
    },
    breadcrumbSeparator: {
      color: '#bbbcbc',
      margin: '0 0.5rem'
    },
    header: {
      background: 'linear-gradient(135deg, #86bc25 0%, #43b024 100%)',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px rgba(134, 188, 37, 0.2)',
      color: '#ffffff',
      position: 'relative',
      overflow: 'hidden'
    },
    headerPattern: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: '200px',
      height: '200px',
      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
      backgroundSize: '20px 20px',
      opacity: 0.3
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      margin: 0,
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'relative',
      zIndex: 1
    },
    subtitle: {
      fontSize: '1.1rem',
      opacity: 0.9,
      margin: '0.5rem 0 0 0',
      fontWeight: '400',
      position: 'relative',
      zIndex: 1
    },
    quickStatsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    statCard: {
      background: '#ffffff',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #f0f0f0',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      textAlign: 'center'
    },
    statIcon: {
      fontSize: '2rem',
      marginBottom: '0.5rem'
    },
    statNumber: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#86bc25',
      margin: '0.25rem 0'
    },
    statLabel: {
      fontSize: '0.85rem',
      color: '#63666a',
      margin: 0,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: '500'
    },
    contentCard: {
      background: '#ffffff',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #f0f0f0'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '1rem'
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: '4px solid #ededed',
      borderTop: '4px solid #86bc25',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    errorTitle: {
      color: '#da291c',
      fontSize: '1.25rem',
      fontWeight: 700,
      margin: 0
    },
    errorMessage: {
      color: '#63666a',
      fontSize: '1rem',
      margin: 0,
      textAlign: 'center',
      maxWidth: '40rem'
    },
    errorActions: {
      display: 'flex',
      gap: '0.75rem',
      marginTop: '0.5rem'
    },
    retryButton: {
      padding: '0.5rem 1.25rem',
      background: '#86bc25',
      color: '#ffffff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 600
    },
    backButton: {
      padding: '0.5rem 1.25rem',
      background: '#ffffff',
      color: '#63666a',
      border: '1px solid #ccc',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 600,
      textDecoration: 'none'
    }
  };

  if (loading) {
    return (
      <div style={pageStyles.container}>
        <div style={pageStyles.contentCard}>
          <div style={pageStyles.loadingContainer}>
            <div style={pageStyles.spinner}></div>
            <p style={{ color: '#63666a', fontSize: '1.1rem' }}>Loading professional information...</p>
          </div>
        </div>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error || !professional) {
    return (
      <div style={pageStyles.container}>
        <div style={pageStyles.contentCard}>
          <div style={pageStyles.loadingContainer}>
            <h2 style={pageStyles.errorTitle}>Could not load this professional</h2>
            <p style={pageStyles.errorMessage}>{error || `No professional found with ID "${id}".`}</p>
            <div style={pageStyles.errorActions}>
              <button type='button' style={pageStyles.retryButton} onClick={() => fetchData()}>
                Try again
              </button>
              <Link to='/professionals' style={pageStyles.backButton}>
                Back to professionals
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate quick stats
  const totalEngagements = engagements ? engagements.length : 0;
  const totalHours = engagements ? engagements.reduce((sum, eng) => sum + (parseInt(eng.Hours) || 0), 0) : 0;
  const activeEngagements = engagements ? engagements.filter(eng => eng.Status === 'Open').length : 0;

  return (
    <div style={pageStyles.container}>
      {/* Main Content */}
      <div style={pageStyles.contentCard}>
        <ProfessionalDetails professional={professional} engagements={engagements} startDates={startDates} onUpdate={handleUpdate} />
      </div>
    </div>
  );
};

export default ProfessionalDetailsPage;
